import React, { useState, useCallback, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Tabs,
  Tab,
  Alert,
  AlertTitle,
  Snackbar,
  CircularProgress,
  Stack,
  Chip,
  TextField,
  Collapse,
  IconButton,
} from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import SaveIcon from '@mui/icons-material/Save';
import SearchIcon from '@mui/icons-material/Search';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import EventIcon from '@mui/icons-material/Event';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs, { Dayjs } from 'dayjs';
import 'dayjs/locale/ko';
import axios from 'axios';
import { useAuth } from '../../services/cognito';
import config from '../../config';
import {
  CircuitVisit,
  CircuitVisitListResponse,
  S13GetResponse,
  S13SaveResponse,
  S13Payload,
  S13Visit,
} from './s13-types';
import S13SheetTable from './S13SheetTable';
import S13CircuitVisitDialog from './S13CircuitVisitDialog';
import {
  clonePayload,
  normalizePayload,
  payloadsEqual,
  hasInvalidDate,
  padVisits,
  sheetLabel,
  validatePeriod,
} from './s13-utils';

type Snack = {
  open: boolean;
  message: string;
  severity: 'success' | 'error' | 'info';
};

/** Content-Disposition 헤더에서 filename 추출 (RFC 5987 / 평문 모두 시도) */
function filenameFromDisposition(disposition?: string): string | null {
  if (!disposition) return null;
  const utf8Match = /filename\*=UTF-8''([^;]+)/i.exec(disposition);
  if (utf8Match) {
    try {
      return decodeURIComponent(utf8Match[1]);
    } catch {
      return utf8Match[1];
    }
  }
  const plainMatch = /filename="?([^";]+)"?/i.exec(disposition);
  return plainMatch ? plainMatch[1] : null;
}

/** 폴백 파일명 S13-<yyyyMMdd>.xlsx */
function fallbackFilename(): string {
  return `S13-${dayjs().format('YYYYMMDD')}.xlsx`;
}

const S13Page: React.FC = () => {
  const { token } = useAuth();

  // 조회 기간 (Dayjs). 기본값: 시작일 = 올해 1월 1일, 종료일 = 오늘
  const [startDate, setStartDate] = useState<Dayjs | null>(
    dayjs().startOf('year'),
  );
  const [endDate, setEndDate] = useState<Dayjs | null>(dayjs());
  // 실제 조회된 payload (편집 상태)
  const [payload, setPayload] = useState<S13Payload | null>(null);
  // 조회 직후 원본 (미저장 변경 판단 기준)
  const [baseline, setBaseline] = useState<S13Payload | null>(null);
  const [source, setSource] = useState<'snapshot' | 'generated' | null>(null);
  const [meta, setMeta] = useState<{ updatedAt?: string; updatedBy?: string }>(
    {},
  );
  const [warnings, setWarnings] = useState<string[]>([]);
  const [warningsOpen, setWarningsOpen] = useState(false);
  const [activeTab, setActiveTab] = useState(0);

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [exporting, setExporting] = useState(false);

  // 순회방문 기록 (최신 종료일 + 1일 = 시작일 자동 지정)
  const [circuitVisits, setCircuitVisits] = useState<CircuitVisit[]>([]);
  const [circuitDialogOpen, setCircuitDialogOpen] = useState(false);
  const [circuitBusy, setCircuitBusy] = useState(false);
  const [snack, setSnack] = useState<Snack>({
    open: false,
    message: '',
    severity: 'success',
  });

  const dirty =
    payload !== null && baseline !== null && !payloadsEqual(payload, baseline);

  // 미저장 변경 상태에서 새로고침·탭 닫기 등 브라우저 이탈 시 경고
  useEffect(() => {
    if (!dirty) return undefined;
    const handler = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      // 일부 브라우저는 returnValue 설정을 요구
      e.returnValue = '';
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [dirty]);

  const showSnack = (message: string, severity: Snack['severity']) =>
    setSnack({ open: true, message, severity });

  const dateString = (d: Dayjs | null): string | null =>
    d && d.isValid() ? d.format('YYYY-MM-DD') : null;

  const fetchPeriod = useCallback(
    async (start: string, end: string) => {
      if (!token) return;
      setLoading(true);
      try {
        const res = await axios.get<S13GetResponse>(
          `${config.api.url}/mdsouth/s13`,
          {
            params: { start, end },
            headers: { Authorization: `Bearer ${token}` },
          },
        );
        const data = res.data;
        setPayload(clonePayload(data.payload));
        setBaseline(clonePayload(data.payload));
        setSource(data.source);
        setMeta({ updatedAt: data.updatedAt, updatedBy: data.updatedBy });
        setWarnings(data.warnings || []);
        setWarningsOpen(false);
        setActiveTab(0);
      } catch (err) {
        showSnack('S-13 데이터를 불러오지 못했습니다', 'error');
      } finally {
        setLoading(false);
      }
    },
    [token],
  );

  /** 순회방문 목록 조회. 반환값으로 최신 판단 (종료일 내림차순) */
  const fetchCircuitVisits = useCallback(async (): Promise<
    CircuitVisit[] | null
  > => {
    if (!token) return null;
    try {
      const res = await axios.get<CircuitVisitListResponse>(
        `${config.api.url}/mdsouth/s13/circuit-visits`,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      setCircuitVisits(res.data.visits);
      return res.data.visits;
    } catch (err) {
      showSnack('순회방문 기록을 불러오지 못했습니다', 'error');
      return null;
    }
  }, [token]);

  // 진입 시 최신 순회방문 종료일 + 1일을 시작일로 자동 지정
  useEffect(() => {
    (async () => {
      const visits = await fetchCircuitVisits();
      const latest = visits?.[0];
      if (latest) {
        setStartDate(dayjs(latest.endDate).add(1, 'day'));
      }
    })();
  }, [fetchCircuitVisits]);

  const handleCircuitAdd = async (
    start: string,
    end: string,
  ): Promise<boolean> => {
    if (!token) return false;
    setCircuitBusy(true);
    try {
      await axios.post(
        `${config.api.url}/mdsouth/s13/circuit-visits`,
        { startDate: start, endDate: end },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      const visits = await fetchCircuitVisits();
      const latest = visits?.[0];
      if (latest) {
        setStartDate(dayjs(latest.endDate).add(1, 'day'));
      }
      showSnack('순회방문 기록을 등록했습니다', 'success');
      return true;
    } catch (err) {
      showSnack('순회방문 기록 등록에 실패했습니다', 'error');
      return false;
    } finally {
      setCircuitBusy(false);
    }
  };

  const handleCircuitDelete = async (id: number) => {
    if (!token) return;
    // eslint-disable-next-line no-alert
    if (!window.confirm('이 순회방문 기록을 삭제하시겠습니까?')) return;
    setCircuitBusy(true);
    try {
      await axios.delete(
        `${config.api.url}/mdsouth/s13/circuit-visits/${id}`,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      const visits = await fetchCircuitVisits();
      const latest = visits?.[0];
      if (latest) {
        setStartDate(dayjs(latest.endDate).add(1, 'day'));
      }
      showSnack('순회방문 기록을 삭제했습니다', 'success');
    } catch (err) {
      // 다른 곳에서 이미 삭제된 기록(404) 등 서버와 어긋난 상태 → 목록 재동기화
      const notFound =
        axios.isAxiosError(err) && err.response?.status === 404;
      await fetchCircuitVisits();
      showSnack(
        notFound
          ? '이미 삭제된 기록입니다. 목록을 새로 불러왔습니다'
          : '순회방문 기록 삭제에 실패했습니다',
        notFound ? 'info' : 'error',
      );
    } finally {
      setCircuitBusy(false);
    }
  };

  const confirmDiscardIfDirty = (): boolean => {
    if (!dirty) return true;
    // eslint-disable-next-line no-alert
    return window.confirm(
      '저장하지 않은 변경 사항이 있습니다. 계속하면 변경 내용이 사라집니다. 계속하시겠습니까?',
    );
  };

  const handleLoad = () => {
    const start = dateString(startDate);
    const end = dateString(endDate);
    if (!start || !end) {
      showSnack('시작일과 종료일을 모두 선택하세요', 'error');
      return;
    }
    const periodError = validatePeriod(start, end);
    if (periodError) {
      showSnack(periodError, 'error');
      return;
    }
    if (!confirmDiscardIfDirty()) return;
    fetchPeriod(start, end);
  };

  const handleStartChange = (value: Dayjs | null) => {
    // 미저장 변경이 있으면 시작/종료일 변경 확인
    if (dirty && !confirmDiscardIfDirty()) return;
    setStartDate(value);
  };

  const handleEndChange = (value: Dayjs | null) => {
    if (dirty && !confirmDiscardIfDirty()) return;
    setEndDate(value);
  };

  // 안정된 콜백(useCallback) + 시트 index 명시 — memo된 시트 테이블이
  // 편집된 시트만 다시 그리도록 참조 동일성을 보장한다.
  const handleVisitChange = useCallback(
    (
      sheetIndex: number,
      rowIndex: number,
      slotIndex: number,
      field: keyof S13Visit,
      value: string,
    ) => {
      setPayload((prev) => {
        if (!prev) return prev;
        const sheets = prev.sheets.map((sheet, sIdx) => {
          if (sIdx !== sheetIndex) return sheet;
          const rows = sheet.rows.map((row, rIdx) => {
            if (rIdx !== rowIndex) return row;
            // 화면은 항상 4슬롯을 노출하므로 4개로 패딩한 배열 위에서 갱신한다.
            // 정규화(빈 슬롯 제거)는 저장/내보내기 직전에 수행.
            const visits = padVisits(row.visits);
            visits[slotIndex] = { ...visits[slotIndex], [field]: value };
            return { ...row, visits };
          });
          return { ...sheet, rows };
        });
        return { ...prev, sheets };
      });
    },
    [],
  );

  const handleSave = async () => {
    if (!payload || !token) return;
    if (hasInvalidDate(payload)) {
      showSnack('방문일 형식 오류가 있습니다. 확인 후 다시 저장하세요', 'error');
      return;
    }
    const normalized = normalizePayload(payload);
    setSaving(true);
    try {
      const res = await axios.put<S13SaveResponse>(
        `${config.api.url}/mdsouth/s13`,
        { payload: normalized },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      setPayload(clonePayload(normalized));
      setBaseline(clonePayload(normalized));
      setSource('snapshot');
      setMeta({ updatedAt: res.data.updatedAt, updatedBy: meta.updatedBy });
      showSnack('저장되었습니다', 'success');
    } catch (err) {
      showSnack('저장에 실패했습니다', 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleExport = async () => {
    if (!payload || !token) return;
    if (hasInvalidDate(payload)) {
      showSnack(
        '방문일 형식 오류가 있습니다. 확인 후 다시 시도하세요',
        'error',
      );
      return;
    }
    const normalized = normalizePayload(payload);
    setExporting(true);
    try {
      const res = await axios.post(
        `${config.api.url}/mdsouth/s13/export`,
        { payload: normalized },
        {
          headers: { Authorization: `Bearer ${token}` },
          responseType: 'blob',
        },
      );
      const filename =
        filenameFromDisposition(
          res.headers['content-disposition'] as string | undefined,
        ) || fallbackFilename();
      const blob = new Blob([res.data], {
        type:
          (res.headers['content-type'] as string) ||
          'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      // export는 서버에서 스냅샷도 upsert → baseline 동기화
      setPayload(clonePayload(normalized));
      setBaseline(clonePayload(normalized));
      setSource('snapshot');
      showSnack('엑셀 파일을 다운로드했습니다', 'success');
    } catch (err) {
      showSnack('엑셀 다운로드에 실패했습니다', 'error');
    } finally {
      setExporting(false);
    }
  };

  const busy = loading || saving || exporting;

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h5" gutterBottom fontWeight="bold">
        S-13 양식
      </Typography>

      {/* 기간 선택 + 조회 */}
      <Stack
        direction="row"
        spacing={1}
        alignItems="center"
        sx={{ mb: 2, flexWrap: 'wrap', gap: 1 }}
      >
        <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="ko">
          <DatePicker
            label="시작일"
            value={startDate}
            onChange={handleStartChange}
            disabled={busy}
            inputFormat="YYYY-MM-DD"
            renderInput={(params) => (
              // renderInput은 x-date-pickers v5 API
              // eslint-disable-next-line react/jsx-props-no-spreading
              <TextField {...params} size="small" />
            )}
          />
          <Typography component="span" sx={{ px: 0.5 }}>
            ~
          </Typography>
          <DatePicker
            label="종료일"
            value={endDate}
            onChange={handleEndChange}
            disabled={busy}
            inputFormat="YYYY-MM-DD"
            renderInput={(params) => (
              // eslint-disable-next-line react/jsx-props-no-spreading
              <TextField {...params} size="small" />
            )}
          />
        </LocalizationProvider>
        <Button
          variant="contained"
          startIcon={<SearchIcon />}
          onClick={handleLoad}
          disabled={busy}
        >
          조회
        </Button>
        <Button
          size="small"
          startIcon={<EventIcon />}
          onClick={() => setCircuitDialogOpen(true)}
          disabled={busy}
        >
          순회방문 관리
        </Button>
        {loading && <CircularProgress size={24} />}
      </Stack>

      {/* 순회방문 기반 시작일 자동 지정 안내 */}
      {circuitVisits.length > 0 && (
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ display: 'block', mt: -1.5, mb: 2 }}
        >
          직전 순회방문 {circuitVisits[0].startDate} ~{' '}
          {circuitVisits[0].endDate} — 시작일이 종료일 다음 날(
          {dayjs(circuitVisits[0].endDate).add(1, 'day').format('YYYY-MM-DD')}
          )로 자동 지정됩니다.
        </Typography>
      )}

      {/* 소스·메타 안내 */}
      {payload && source === 'snapshot' && (
        <Alert severity="info" sx={{ mb: 2 }}>
          저장본을 표시합니다.
          {meta.updatedAt &&
            ` (최종 수정: ${dayjs(meta.updatedAt).format('YYYY-MM-DD HH:mm')}`}
          {meta.updatedBy ? `, ${meta.updatedBy})` : meta.updatedAt ? ')' : ''}
        </Alert>
      )}
      {payload && source === 'generated' && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          미저장 — 방문 기록 기반 자동 생성. 저장 전까지는 스냅샷이 없습니다.
        </Alert>
      )}

      {/* 서버 경고 — 기본 접힘, 클릭으로 펼침 */}
      {warnings.length > 0 && (
        <Alert
          severity="warning"
          sx={{ mb: 2, cursor: 'pointer' }}
          onClick={() => setWarningsOpen((open) => !open)}
          action={
            <IconButton
              aria-label={warningsOpen ? '경고 접기' : '경고 펼치기'}
              aria-expanded={warningsOpen}
              color="inherit"
              size="small"
            >
              <ExpandMoreIcon
                sx={{
                  transform: warningsOpen ? 'rotate(180deg)' : 'none',
                  transition: 'transform 0.2s',
                }}
              />
            </IconButton>
          }
        >
          <AlertTitle sx={{ mb: 0 }}>
            경고 {warnings.length}건 {warningsOpen ? '' : '— 클릭해서 펼치기'}
          </AlertTitle>
          <Collapse in={warningsOpen}>
            <ul style={{ margin: '8px 0 0', paddingLeft: 18 }}>
              {warnings.map((w) => (
                <li key={w}>{w}</li>
              ))}
            </ul>
          </Collapse>
        </Alert>
      )}

      {/* 편집 영역 */}
      {payload && (
        <>
          <Stack
            direction="row"
            spacing={1}
            alignItems="center"
            sx={{ mb: 1, flexWrap: 'wrap', gap: 1 }}
          >
            <Typography variant="body2" color="text.secondary">
              조회 기간 {payload.startDate} ~ {payload.endDate}
            </Typography>
            {dirty && (
              <Chip label="미저장 변경" color="warning" size="small" />
            )}
            <Box sx={{ flexGrow: 1 }} />
            <Button
              variant="outlined"
              startIcon={<SaveIcon />}
              onClick={handleSave}
              disabled={busy || !dirty}
            >
              {saving ? '저장 중...' : '저장'}
            </Button>
            <Button
              variant="contained"
              startIcon={<DownloadIcon />}
              onClick={handleExport}
              disabled={busy}
            >
              {exporting ? '생성 중...' : '엑셀 다운로드'}
            </Button>
          </Stack>

          <Tabs
            value={activeTab}
            onChange={(_, v: number) => setActiveTab(v)}
            variant="scrollable"
            scrollButtons="auto"
            sx={{ borderBottom: 1, borderColor: 'divider' }}
          >
            {payload.sheets.map((sheet) => (
              <Tab key={sheet.sheetNo} label={sheetLabel(sheet.sheetNo)} />
            ))}
          </Tabs>

          {/* 활성 시트만 mount — 숨김 유지(keep-mounted)는 누적 DOM이
              커질수록 새 탭 mount가 초선형으로 느려져(측정 검증) 사용하지 않는다.
              페이지네이션(20행)으로 mount 비용 자체를 작게 유지. */}
          {payload.sheets[activeTab] && (
            <S13SheetTable
              key={payload.sheets[activeTab].sheetNo}
              sheet={payload.sheets[activeTab]}
              sheetIndex={activeTab}
              onVisitChange={handleVisitChange}
              disabled={busy}
            />
          )}
        </>
      )}

      {!payload && !loading && (
        <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
          조회할 기간(시작일 ~ 종료일)을 선택하고 조회하세요.
        </Typography>
      )}

      <S13CircuitVisitDialog
        open={circuitDialogOpen}
        visits={circuitVisits}
        busy={circuitBusy}
        onClose={() => setCircuitDialogOpen(false)}
        onAdd={handleCircuitAdd}
        onDelete={handleCircuitDelete}
      />

      <Snackbar
        open={snack.open}
        autoHideDuration={4000}
        onClose={() => setSnack((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          severity={snack.severity}
          onClose={() => setSnack((s) => ({ ...s, open: false }))}
        >
          {snack.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default S13Page;
